import { useMemo, useState } from "react";
import { Box, Typography, TextField, Button, Stack, MenuItem } from "@mui/material";
import { tokens } from "../../theme";
import { useAuth } from "../../AuthContext";

// ICON
import GroupsIcon from "@mui/icons-material/Groups";
import VideocamIcon from "@mui/icons-material/Videocam";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HandymanIcon from "@mui/icons-material/Handyman";
import SchoolIcon from "@mui/icons-material/School";

// COMPONENTS
import TargetDesc from "../../components/targets/TargetDesc";
import PageGuard from "../../components/PageGuard";
import LoadingState from "../../components/LoadingState";

// UTILS
import { useRedfidViews } from "../../utils/redfidView";
import { DISCIPLINE_LABEL } from "../../utils/mapers";
import {
  EMPTY_ARRAY,
  getItemYear,
  getList,
  isYearInRange,
  normalizeText,
  normalizeYear,
  safeDate,
  withYearRangeLabel,
  yearRangeLabel,
} from "../../utils/utils";

const iconByKey = (key, colors) => {
  const sx = { color: colors.green[200] };
  if (key === "videocam") return <VideocamIcon sx={sx} />;
  if (key === "eventNote") return <EventNoteIcon sx={sx} />;
  if (key === "handyman") return <HandymanIcon sx={sx} />;
  if (key === "school") return <SchoolIcon sx={sx} />;
  return <GroupsIcon sx={sx} />;
};

const gridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(1, 1fr)",
    sm: "repeat(2, 1fr)",
    md: "repeat(3, 1fr)",
  },
  gap: "20px",
};

const titleSlot = (colors, text) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      pb: 2,
    }}
  >
    <Typography
      variant="h4"
      fontWeight={600}
      color={colors.primary[100]}
      sx={{ m: 0 }}
    >
      {text}
    </Typography>
  </Box>
);

const renderName = (colors) => (name) => (
  <Typography
    sx={{
      fontWeight: 900,
      fontSize: "13.5px",
      color: colors.primary[100],
    }}
  >
    {name}
  </Typography>
);

const commonTargetProps = (colors, canSeeList) => ({
  variant: "dash",
  fullWidth: true,
  bgColor: colors.primary[200],
  sx: {
    minHeight: 84,
    borderRadius: "18px",
  },
  maxHeight: 260,
  renderItem: renderName(colors),
  expandedDivider: false,
  canViewList: canSeeList,
  hideListWhenEmpty: true,
});

const safeStr = (value, fallback = "—") =>
  String(value ?? "").trim() || fallback;

function getDisciplineLabel(discipline) {
  const key = normalizeText(discipline);
  return (DISCIPLINE_LABEL[key] ?? DISCIPLINE_LABEL.general).toUpperCase();
}

function getEventDisciplines(event) {
  const arr = Array.isArray(event?.disciplinas) ? event.disciplinas : [];
  const single = normalizeText(event?.disciplina);
  return [...arr.map(normalizeText), single].filter(Boolean);
}

function eventHasDiscipline(event, discipline) {
  const wanted = normalizeText(discipline);
  const disciplines = getEventDisciplines(event);
  return disciplines.includes(wanted);
}

function sortEventsByDateAsc(events) {
  return [...events].sort((a, b) => {
    const ta =
      safeDate(
        a?.inicio ?? a?.start_date ?? a?.fecha ?? a?.date ?? a?.event_date ?? null
      )?.getTime() ?? Number.POSITIVE_INFINITY;
    const tb =
      safeDate(
        b?.inicio ?? b?.start_date ?? b?.fecha ?? b?.date ?? b?.event_date ?? null
      )?.getTime() ?? Number.POSITIVE_INFINITY;

    if (ta !== tb) return ta - tb;

    return safeStr(a?.titulo ?? a?.title, "").localeCompare(
      safeStr(b?.titulo ?? b?.title, "")
    );
  });
}

function sortPresentacionesByDateAsc(rows) {
  return [...rows].sort((a, b) => {
    const ta =
      safeDate(
        a?.inicio ?? a?.start_date ?? a?.fecha ?? a?.date ?? a?.event_date ?? null
      )?.getTime() ?? Number.POSITIVE_INFINITY;
    const tb =
      safeDate(
        b?.inicio ?? b?.start_date ?? b?.fecha ?? b?.date ?? b?.event_date ?? null
      )?.getTime() ?? Number.POSITIVE_INFINITY;

    if (ta !== tb) return ta - tb;

    return safeStr(a?.encuentro, "").localeCompare(safeStr(b?.encuentro, ""));
  });
}

function PresentacionesSection({ title, iconKey, valueLabel, rows, colors, canSeeList }) {
  const list = sortPresentacionesByDateAsc(Array.isArray(rows) ? rows : []);
  if (!list.length) return null;

  return (
    <Box mt={4}>
      {titleSlot(colors, title)}
      <Box sx={gridSx}>
        {list.map((r) => {
          const id = String(r?.id ?? r?.encuentro ?? "");
          const names = Array.isArray(r?.list) ? r.list : [];
          const count = Number(r?.count ?? names.length ?? 0);

          return (
            <TargetDesc
              key={id}
              {...commonTargetProps(colors, canSeeList)}
              title={safeStr(r?.encuentro ?? "Encuentro")}
              value={count}
              valueLabel={valueLabel}
              icon={iconByKey(iconKey, colors)}
              description=""
              items={names}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function EventsSectionFromApi({
  title,
  iconKey,
  valueLabel,
  bucket,
  eventsTable,
  attendeesAgg,
  colors,
  canSeeList,
  onlyWithParticipants = true,
  discipline = null,
}) {
  const bucketNormWanted = bucket ? normalizeText(bucket) : null;

  let list = (eventsTable ?? [])
    .filter((e) => normalizeText(e?.categoria ?? e?.category) === "redfid")
    .filter((e) =>
      bucketNormWanted ? normalizeText(e?.bucket) === bucketNormWanted : true
    )
    .filter((e) => {
      if (!discipline) return true;
      return eventHasDiscipline(e, discipline);
    });


  if (onlyWithParticipants) {
    list = list.filter((e) => getList(attendeesAgg, String(e?.id ?? "")).length > 0);
  }

  list = sortEventsByDateAsc(list);

  if (!list.length) return null;

  return (
    <Box mt={4}>
      {titleSlot(colors, title)}
      <Box sx={gridSx}>
        {list.map((e) => {
          const id = String(e?.id ?? "");
          const names = getList(attendeesAgg, id);
          const count = names.length;
          const bucketNorm = normalizeText(e?.bucket);
          const rawTitle = e?.titulo;

          let cardTitle = safeStr(rawTitle);
          let expandedSubtitle = safeStr(e?.lugar, "");

          if (bucketNorm === "taller") {
            cardTitle = safeStr(e?.taller_card_title ?? "Taller");
            expandedSubtitle = safeStr(e?.taller_card_subtitle ?? rawTitle, "");
          }

          const rawDescription = safeStr(e?.description ?? e?.descripcion, "");

          const description = rawDescription ? (
            <Box
              component="span"
              sx={{
                "& p": { m: 0, mb: 1 },
                "& ul": { mt: 0.5, mb: 1, pl: 2.5 },
                "& li": { mb: 0.5 },
                "& br": { display: "block", content: '""', mb: 0.5 },
              }}
              dangerouslySetInnerHTML={{
                __html: rawDescription.includes("<")
                  ? rawDescription
                  : rawDescription.replace(/\n/g, "<br />"),
              }}
            />
          ) : "";
          const expositores = safeStr(e?.expositor ?? e?.expositores ?? e?.relator, "");
          const formadores =
            bucketNorm === "taller" || bucketNorm === "webinar" ? expositores : "";

          return (
            <TargetDesc
              key={id}
              {...commonTargetProps(colors, canSeeList)}
              title={cardTitle}
              descriptionTitle={expandedSubtitle}
              formadores={formadores}
              value={count}
              valueLabel={valueLabel}
              icon={iconByKey(iconKey, colors)}
              description={description}
              items={names}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default function Eventos() {
  const auth = useAuth();
  const colors = tokens();
  const { loading, error, data } = useRedfidViews();

  const canSeeList = !!auth?.user?.is_admin || !!auth?.user?.is_staff;
  const canSeeCommitteeActivities = !!auth?.user?.is_admin || !!auth?.user?.is_staff;

  const [startYearInput, setStartYearInput] = useState("");
  const [endYearInput, setEndYearInput] = useState("");
  const [appliedStartYear, setAppliedStartYear] = useState(null);
  const [appliedEndYear, setAppliedEndYear] = useState(null);

  const eventosTable = data?.tables?.eventos ?? EMPTY_ARRAY;
  const eventosConAsistentes = data?.views?.eventosConAsistentes ?? EMPTY_ARRAY;
  const cursosCatalogo = data?.views?.cursosCatalogo ?? EMPTY_ARRAY;
  const talleresPorAnio = data?.views?.talleresPorAnio ?? EMPTY_ARRAY;
  const presentaciones = data?.views?.presentaciones ?? EMPTY_ARRAY;
  const discipline = data?.discipline ?? "general";


  const attendeesAgg = (eventosConAsistentes ?? [])
    .map((x) => {
      const id = String(x?.event_id ?? "");
      const attendees = Array.isArray(x?.attendees) ? x.attendees : [];

      const names = attendees
        .map((a) =>
          safeStr(a?.nombre_completo ?? `${a?.nombre ?? ""} ${a?.apellido ?? ""}`.trim(), "")
        )
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

      return { id, count: names.length, list: names };
    })
    .filter((x) => x.id);

  const appliedLabel = yearRangeLabel(appliedStartYear, appliedEndYear);

  const eventosFiltrados = useMemo(
    () =>
      (eventosTable ?? [])
        .filter((e) => isYearInRange(getItemYear(e), appliedStartYear, appliedEndYear))
        .filter((e) => normalizeText(e?.categoria ?? e?.category) === "redfid")
        .filter((e) => {
          if (normalizeText(e?.bucket) === "actividad-comite" && !canSeeCommitteeActivities) {
            return false;
          }
          return true;
        }),
    [eventosTable, appliedStartYear, appliedEndYear, canSeeCommitteeActivities]
  );

  const presentacionesFiltradas = useMemo(
    () =>
      (presentaciones ?? []).filter((p) =>
        isYearInRange(getItemYear(p), appliedStartYear, appliedEndYear)
      ),
    [presentaciones, appliedStartYear, appliedEndYear]
  );

  const cursosCatalogoFiltrado = useMemo(
    () =>
      cursosCatalogo
        .map((course) => ({
          ...course,
          sections: (Array.isArray(course?.sections) ? course.sections : [])
            .filter((section) =>
              isYearInRange(section?.year, appliedStartYear, appliedEndYear)
            )
            .filter((section) =>
              Number(section?.aprobados_qtty ?? section?.count ?? 0) > 0
            ),
        }))
        .filter((course) => course.sections.length > 0),
    [cursosCatalogo, appliedStartYear, appliedEndYear]
  );

  const talleresGroupsFiltrados = useMemo(
    () =>
      (talleresPorAnio ?? [])
        .filter((g) => isYearInRange(g?.year, appliedStartYear, appliedEndYear))
        .filter((g) => Number.isFinite(Number(g?.year)))
        .sort((a, b) => {
          const yearDiff = Number(b.year) - Number(a.year);
          if (yearDiff) return yearDiff;

          const order = {
            matematica: 0,
            lenguaje: 1,
          };

          return (order[normalizeText(a?.discipline)] ?? 9) - (order[normalizeText(b?.discipline)] ?? 9);
        }),
    [talleresPorAnio, appliedStartYear, appliedEndYear]
  );

  if (loading) return <LoadingState message="" />;
  if (error) return <div>Error cargando datos: {String(error)}</div>;



  const hasAnyContent =
    eventosFiltrados.length > 0 ||
    presentacionesFiltradas.length > 0 ||
    cursosCatalogoFiltrado.length > 0 ||
    talleresGroupsFiltrados.some((g) => Array.isArray(g?.talleres) && g.talleres.length > 0);

  const handleApplyFilter = () => {
    const start = normalizeYear(startYearInput);
    const end = normalizeYear(endYearInput);

    if (start != null && end != null && end < start) {
      setAppliedStartYear(end);
      setAppliedEndYear(start);
      setStartYearInput(String(end));
      setEndYearInput(String(start));
      return;
    }

    setAppliedStartYear(start);
    setAppliedEndYear(end);
  };

  const handleClearFilter = () => {
    setStartYearInput("");
    setEndYearInput("");
    setAppliedStartYear(null);
    setAppliedEndYear(null);
  };

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from(
    { length: currentYear - 2022 + 1 },
    (_, i) => String(2022 + i)
  );

  return (
    <PageGuard title="ACTIVIDADES" subtitle="Participación por instancia">
      <Box>
        <Box
          sx={{
            mt: 2,
            borderRadius: "18px",
            backgroundColor: colors.primary[200],
          }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            color={colors.primary[100]}
            mb={2}
          >
            Filtrar por años
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              select
              label="Año inicio"
              value={startYearInput}
              onChange={(e) => setStartYearInput(e.target.value)}
              size="small"
              sx={{
                minWidth: 160,
                "& .MuiInputBase-root": {
                  backgroundColor: colors.primary[200],
                },
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={`start-${year}`} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Año fin (opcional)"
              value={endYearInput}
              onChange={(e) => setEndYearInput(e.target.value)}
              size="small"
              sx={{
                minWidth: 180,
                "& .MuiInputBase-root": {
                  backgroundColor: colors.primary[200],
                },
              }}
            >
              <MenuItem value="">Sin fin</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={`end-${year}`} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="span"
              onClick={handleApplyFilter}
              sx={{
                height: 40,
                px: 3,
                fontWeight: 700,
                color: colors.primary[100],
                backgroundColor: colors.green[100],
                "&:hover": {
                  backgroundColor: colors.green[200],
                },
              }}
            >
              Aplicar
            </Button>

            <Button
              variant="span"
              onClick={handleClearFilter}
              sx={{
                height: 40,
                px: 3,
                fontWeight: 700,
                color: colors.primary[100],
                backgroundColor: colors.green[100],
                "&:hover": {
                  backgroundColor: colors.green[200],
                },
              }}
            >
              Limpiar
            </Button>
          </Stack>

          <Typography mt={2} color={colors.primary[100]} fontSize="14px">
            Mostrando: <strong>{appliedLabel}</strong>
          </Typography>
        </Box>

        {!hasAnyContent && (
          <Typography color={colors.primary[100]} fontWeight={600}>
            No hay actividades para el rango seleccionado.
          </Typography>
        )}

        {normalizeText(discipline) === "general" ? (
          <>
            <EventsSectionFromApi
              title={withYearRangeLabel(
                "ENCUENTROS NACIONALES DE FORMADORES DE PROFESORES EN MATEMÁTICA",
                appliedStartYear,
                appliedEndYear
              )}
              iconKey="groups"
              valueLabel={{
                singular: "persona",
                plural: "personas",
              }}
              bucket="encuentro-nacional"
              eventsTable={eventosFiltrados.filter(
                (e) => eventHasDiscipline(e, "matematica") || eventHasDiscipline(e, "parvularia")
              )}
              attendeesAgg={attendeesAgg}
              colors={colors}
              canSeeList={canSeeList}
            />

            <EventsSectionFromApi
              title={withYearRangeLabel(
                "ENCUENTROS NACIONALES DE FORMADORES DE PROFESORES EN LENGUAJE",
                appliedStartYear,
                appliedEndYear
              )}
              iconKey="groups"
              valueLabel={{
                singular: "persona",
                plural: "personas",
              }}
              bucket="encuentro-nacional"
              eventsTable={eventosFiltrados}
              attendeesAgg={attendeesAgg}
              colors={colors}
              canSeeList={canSeeList}
              discipline="lenguaje"
            />

            <EventsSectionFromApi
              title={withYearRangeLabel(
                "ENCUENTROS TELEMÁTICOS DE FORMADORES DE PROFESORES EN MATEMÁTICA",
                appliedStartYear,
                appliedEndYear
              )}
              iconKey="groups"
              valueLabel={{
                singular: "persona",
                plural: "personas",
              }}
              bucket="encuentro-virtual"
              eventsTable={eventosFiltrados}
              attendeesAgg={attendeesAgg}
              colors={colors}
              canSeeList={canSeeList}
              discipline="matematica"
            />

            <EventsSectionFromApi
              title={withYearRangeLabel(
                "ENCUENTROS TELEMÁTICOS DE FORMADORES DE PROFESORES EN LENGUAJE",
                appliedStartYear,
                appliedEndYear
              )}
              iconKey="groups"
              valueLabel={{
                singular: "persona",
                plural: "personas",
              }}
              bucket="encuentro-virtual"
              eventsTable={eventosFiltrados}
              attendeesAgg={attendeesAgg}
              colors={colors}
              canSeeList={canSeeList}
              discipline="lenguaje"
            />

            <EventsSectionFromApi
              title={withYearRangeLabel(
                "ENCUENTROS TELEMÁTICOS DE FORMADORES DE PROFESORES EN PARVULARIA",
                appliedStartYear,
                appliedEndYear
              )}
              iconKey="groups"
              valueLabel={{
                singular: "persona",
                plural: "personas",
              }}
              bucket="encuentro-virtual"
              eventsTable={eventosFiltrados}
              attendeesAgg={attendeesAgg}
              colors={colors}
              canSeeList={canSeeList}
              discipline="parvularia"
            />
          </>
        ) : (
          <>
            {normalizeText(discipline) === "lenguaje" && (
              <>
                <EventsSectionFromApi
                  title={withYearRangeLabel(
                    "ENCUENTROS NACIONALES DE FORMADORES DE PROFESORES EN LENGUAJE",
                    appliedStartYear,
                    appliedEndYear
                  )}
                  iconKey="groups"
                  valueLabel={{
                    singular: "persona",
                    plural: "personas",
                  }}
                  bucket="encuentro-nacional"
                  eventsTable={eventosFiltrados}
                  attendeesAgg={attendeesAgg}
                  colors={colors}
                  canSeeList={canSeeList}
                  discipline="lenguaje"
                />

                <EventsSectionFromApi
                  title={withYearRangeLabel(
                    "ENCUENTROS TELEMÁTICOS DE FORMADORES DE PROFESORES EN LENGUAJE",
                    appliedStartYear,
                    appliedEndYear
                  )}
                  iconKey="groups"
                  valueLabel={{
                    singular: "persona",
                    plural: "personas",
                  }}
                  bucket="encuentro-virtual"
                  eventsTable={eventosFiltrados}
                  attendeesAgg={attendeesAgg}
                  colors={colors}
                  canSeeList={canSeeList}
                  discipline="lenguaje"
                />
              </>
            )}

            {normalizeText(discipline) === "parvularia" && (
              <>
                <EventsSectionFromApi
                  title={withYearRangeLabel(
                    "ENCUENTROS NACIONALES DE FORMADORES DE PROFESORES EN MATEMÁTICA",
                    appliedStartYear,
                    appliedEndYear
                  )}
                  iconKey="groups"
                  valueLabel={{
                    singular: "persona",
                    plural: "personas",
                  }}
                  bucket="encuentro-nacional"
                  eventsTable={eventosFiltrados.filter(
                    (e) => eventHasDiscipline(e, "matematica") || eventHasDiscipline(e, "parvularia")
                  )}
                  attendeesAgg={attendeesAgg}
                  colors={colors}
                  canSeeList={canSeeList}
                />

                <EventsSectionFromApi
                  title={withYearRangeLabel(
                    "ENCUENTROS TELEMÁTICOS DE FORMADORES DE PROFESORES EN PARVULARIA",
                    appliedStartYear,
                    appliedEndYear
                  )}
                  iconKey="groups"
                  valueLabel={{
                    singular: "persona",
                    plural: "personas",
                  }}
                  bucket="encuentro-virtual"
                  eventsTable={eventosFiltrados}
                  attendeesAgg={attendeesAgg}
                  colors={colors}
                  canSeeList={canSeeList}
                  discipline="parvularia"
                />
              </>
            )}

            {normalizeText(discipline) === "matematica" && (
              <>
                <EventsSectionFromApi
                  title={withYearRangeLabel(
                    "ENCUENTROS NACIONALES DE FORMADORES DE PROFESORES EN MATEMÁTICA",
                    appliedStartYear,
                    appliedEndYear
                  )}
                  iconKey="groups"
                  valueLabel={{
                    singular: "persona",
                    plural: "personas",
                  }}
                  bucket="encuentro-nacional"
                  eventsTable={eventosFiltrados.filter(
                    (e) => eventHasDiscipline(e, "matematica") || eventHasDiscipline(e, "parvularia")
                  )}
                  attendeesAgg={attendeesAgg}
                  colors={colors}
                  canSeeList={canSeeList}
                />
                <EventsSectionFromApi
                  title={withYearRangeLabel(
                    "ENCUENTROS TELEMÁTICOS DE FORMADORES DE PROFESORES EN MATEMÁTICA",
                    appliedStartYear,
                    appliedEndYear
                  )}
                  iconKey="groups"
                  valueLabel={{
                    singular: "persona",
                    plural: "personas",
                  }}
                  bucket="encuentro-virtual"
                  eventsTable={eventosFiltrados}
                  attendeesAgg={attendeesAgg}
                  colors={colors}
                  canSeeList={canSeeList}
                  discipline="matematica"
                />
              </>
            )}
          </>
        )}

        <PresentacionesSection
          title={withYearRangeLabel(
            normalizeText(discipline) === "general"
              ? "PRESENTACIONES EN ENCUENTROS NACIONALES REDFID"
              : `PRESENTACIONES EN ENCUENTROS NACIONALES REDFID ${getDisciplineLabel(discipline)}`,
            appliedStartYear,
            appliedEndYear
          )}
          iconKey="groups"
          valueLabel="presentadores"
          rows={presentacionesFiltradas}
          colors={colors}
          canSeeList={canSeeList}
        />

        {Array.isArray(cursosCatalogoFiltrado) && cursosCatalogoFiltrado.length > 0 && (
          <Box mt={4}>
            {titleSlot(colors, withYearRangeLabel("CURSOS", appliedStartYear, appliedEndYear))}
            <Box sx={gridSx}>
              {cursosCatalogoFiltrado.flatMap((course) =>
                (Array.isArray(course?.sections) ? course.sections : []).map((section) => {
                  const disciplineLabel =
                    normalizeText(course?.type) === "matematica"
                      ? "Matemática"
                      : normalizeText(course?.type) === "lenguaje"
                        ? "Lenguaje"
                        : normalizeText(course?.type) === "parvularia"
                          ? "Parvularia"
                          : normalizeText(course?.type) === "general"
                            ? ""
                            : safeStr(course?.type, "");

                  const title = [
                    safeStr(course?.name, ""),
                    disciplineLabel,
                    safeStr(section?.year, ""),
                  ]
                    .filter(Boolean)
                    .join(" · ");

                  return (
                    <TargetDesc
                      key={`${course?.course_key ?? "course"}-${section?.section_key ?? section?.course_id ?? Math.random()}`}
                      {...commonTargetProps(colors, canSeeList)}
                      title={title}
                      value={Number(section?.aprobados_qtty ?? section?.count ?? 0)}
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      icon={iconByKey("school", colors)}
                      description={safeStr(
                        section?.description ??
                        course?.description ??
                        section?.special_components?.description,
                        ""
                      )}
                      items={Array.isArray(section?.aprobados) ? section.aprobados : section?.list ?? []}
                    />
                  );
                })
              )}
            </Box>
          </Box>
        )}

        {talleresGroupsFiltrados.map((g) => {
          const year = String(g?.year ?? "");
          const discipline = String(g?.discipline ?? "");
          const talleres = Array.isArray(g?.talleres) ? g.talleres : [];

          return (
            <EventsSectionFromApi
              key={`talleres-${year}-${discipline}`}
              title={safeStr(
                g?.title,
                `TALLERES DE APRENDIZAJE PROFESIONAL - RedFID ${safeStr(
                  g?.discipline_label,
                  ""
                )} ${year}`
              )}
              iconKey="handyman"
              valueLabel={{
                singular: "persona",
                plural: "personas",
              }}
              bucket={null}
              eventsTable={talleres}
              attendeesAgg={attendeesAgg}
              colors={colors}
              canSeeList={canSeeList}
            />
          );
        })}

        <EventsSectionFromApi
          title={withYearRangeLabel("WEBINARS", appliedStartYear, appliedEndYear)}
          iconKey="videocam"
          valueLabel={{
            singular: "persona",
            plural: "personas",
          }}
          bucket="webinar"
          eventsTable={eventosFiltrados}
          attendeesAgg={attendeesAgg}
          colors={colors}
          canSeeList={canSeeList}
        />

        <EventsSectionFromApi
          title={withYearRangeLabel("REUNIONES", appliedStartYear, appliedEndYear)}
          iconKey="eventNote"
          valueLabel={{
            singular: "persona",
            plural: "personas",
          }}
          bucket="reunion-virtual"
          eventsTable={eventosFiltrados}
          attendeesAgg={attendeesAgg}
          colors={colors}
          canSeeList={canSeeList}
        />

        {canSeeCommitteeActivities && (
          <EventsSectionFromApi
            title={withYearRangeLabel("ACTIVIDADES DE COMITÉ", appliedStartYear, appliedEndYear)}
            iconKey="eventNote"
            valueLabel={{
              singular: "persona",
              plural: "personas",
            }}
            bucket="actividad-comite"
            eventsTable={eventosFiltrados}
            attendeesAgg={attendeesAgg}
            colors={colors}
            canSeeList={canSeeList}
          />
        )}
      </Box>
    </PageGuard>
  );
}